import { Placement } from '@popperjs/core';
import * as React from 'react';
import * as TypeStyle from 'typestyle';
import { TabStyles } from '../../styles/TabStyles';
import { HorizontalOriginOption, OverlayProps, VerticalOriginOption } from '../../utils/Overlay';

export const DefaultMaxWidth: number = 300;

export const TooltipDistanceFromAnchor: number = 6;

const BorderWidth: number = 1;
const Padding: number = 12;
export const AggregateTooltipPadding: number = 2 * (BorderWidth + Padding);

export type TooltipTheme = Pick<React.CSSProperties, 'backgroundColor' | 'border' | 'borderRadius' | 'boxShadow' | 'color' | 'fontSize'>;
/** @deprecated This is intended for use only by Tableau Pulse. Please contact #tab-dev-shared-widgets if you need it in another place. */
export const TooltipThemeContext: React.Context<TooltipTheme> = React.createContext<TooltipTheme>({});

const TooltipStyle: React.CSSProperties = {
  backgroundColor: 'white',
  border: `${BorderWidth}px solid #d4d4d4`,
  borderRadius: 1,
  boxShadow: TabStyles.Shadow.Elevated,
  boxSizing: 'border-box',
  display: 'block',
  fontSize: 12,
  maxWidth: '100vw',
  lineHeight: 'normal',
  overflow: 'hidden',
  padding: Padding,
  pointerEvents: 'none', // if space constraints cause tooltip to overlap trigger, prevent immediate dismissal via mouse"leaving" the trigger onto the tooltip
};

export const TooltipStyleName = TypeStyle.style(TooltipStyle);

export enum TooltipPosition {
  TopLeftCorner = 'TopLeftCorner',
  Top = 'Top',
  TopRightCorner = 'TopRightCorner',
  Right = 'Right',
  BottomRightCorner = 'BottomRightCorner',
  Bottom = 'Bottom',
  BottomLeftCorner = 'BottomLeftCorner',
  Left = 'Left',
}

export type TooltipPositionInfo = Pick<OverlayProps, 'anchorOrigin' | 'stackedOrigin'>

export const TooltipPositionInfoMap: { [key in TooltipPosition]: TooltipPositionInfo } = {
  [TooltipPosition.TopLeftCorner]: {
    anchorOrigin: { horizontal: HorizontalOriginOption.left, vertical: VerticalOriginOption.top },
    stackedOrigin: { horizontal: HorizontalOriginOption.right, vertical: VerticalOriginOption.bottom },
  },
  [TooltipPosition.Top]: {
    anchorOrigin: { horizontal: HorizontalOriginOption.centerX, vertical: VerticalOriginOption.top },
    stackedOrigin: { horizontal: HorizontalOriginOption.centerX, vertical: VerticalOriginOption.bottom },
  },
  [TooltipPosition.TopRightCorner]: {
    anchorOrigin: { horizontal: HorizontalOriginOption.right, vertical: VerticalOriginOption.top },
    stackedOrigin: { horizontal: HorizontalOriginOption.left, vertical: VerticalOriginOption.bottom },
  },
  [TooltipPosition.Right]: {
    anchorOrigin: { horizontal: HorizontalOriginOption.right, vertical: VerticalOriginOption.centerY },
    stackedOrigin: { horizontal: HorizontalOriginOption.left, vertical: VerticalOriginOption.centerY },
  },
  [TooltipPosition.BottomRightCorner]: {
    anchorOrigin: { horizontal: HorizontalOriginOption.right, vertical: VerticalOriginOption.bottom },
    stackedOrigin: { horizontal: HorizontalOriginOption.left, vertical: VerticalOriginOption.top },
  },
  [TooltipPosition.Bottom]: {
    anchorOrigin: { horizontal: HorizontalOriginOption.centerX, vertical: VerticalOriginOption.bottom },
    stackedOrigin: { horizontal: HorizontalOriginOption.centerX, vertical: VerticalOriginOption.top },
  },
  [TooltipPosition.BottomLeftCorner]: {
    anchorOrigin: { horizontal: HorizontalOriginOption.left, vertical: VerticalOriginOption.bottom },
    stackedOrigin: { horizontal: HorizontalOriginOption.right, vertical: VerticalOriginOption.top },
  },
  [TooltipPosition.Left]: {
    anchorOrigin: { horizontal: HorizontalOriginOption.left, vertical: VerticalOriginOption.centerY },
    stackedOrigin: { horizontal: HorizontalOriginOption.right, vertical: VerticalOriginOption.centerY },
  },
};

export const TooltipPlacementMap: { [key in TooltipPosition]: Placement } = {
  Bottom: 'bottom',
  BottomLeftCorner: 'bottom-start',
  BottomRightCorner: 'bottom-end',
  Left: 'left',
  Right: 'right',
  Top: 'top',
  TopLeftCorner: 'top-start',
  TopRightCorner: 'top-end',
};

export function adjustClientRectForOffset(clientRect: ClientRect): ClientRect {
  return {
    bottom: clientRect.bottom + TooltipDistanceFromAnchor,
    height: clientRect.height + (2 * TooltipDistanceFromAnchor),
    left: clientRect.left - TooltipDistanceFromAnchor,
    right: clientRect.right + TooltipDistanceFromAnchor,
    top: clientRect.top - TooltipDistanceFromAnchor,
    width: clientRect.width + (2 * TooltipDistanceFromAnchor),
  };
}
